/** Pure derivations behind the lead page — ports of the Nurture app's lead
 *  profile rules (LeadProfileViewModel, DiscoveryFields, UnderstandingSection,
 *  TagStrip, Timeline, NriLocalTime) so a lead reads the same on both surfaces. */
import { Activity, CallWindow, Lead, LeadEngagementLink, Task, Understanding } from '../../types';
import { fmtDayTime, isLate, parseCoreTime } from '../../lib';

// ── Identity ────────────────────────────────────────────────────────────────

// Longest prefix wins. Multi-timezone countries carry no zone: showing a guessed
// time would be worse than showing none.
const NRI_PREFIXES: [string, string, string | null][] = ([
  ['+971', 'UAE', 'Asia/Dubai'], ['+966', 'Saudi Arabia', 'Asia/Riyadh'], ['+965', 'Kuwait', 'Asia/Kuwait'],
  ['+973', 'Bahrain', 'Asia/Bahrain'], ['+974', 'Qatar', 'Asia/Qatar'], ['+968', 'Oman', 'Asia/Muscat'],
  ['+65', 'Singapore', 'Asia/Singapore'], ['+60', 'Malaysia', 'Asia/Kuala_Lumpur'], ['+66', 'Thailand', 'Asia/Bangkok'],
  ['+852', 'Hong Kong', 'Asia/Hong_Kong'], ['+81', 'Japan', 'Asia/Tokyo'], ['+82', 'South Korea', 'Asia/Seoul'],
  ['+44', 'UK', 'Europe/London'], ['+49', 'Germany', 'Europe/Berlin'], ['+33', 'France', 'Europe/Paris'],
  ['+31', 'Netherlands', 'Europe/Amsterdam'], ['+41', 'Switzerland', 'Europe/Zurich'], ['+39', 'Italy', 'Europe/Rome'],
  ['+353', 'Ireland', 'Europe/Dublin'], ['+27', 'South Africa', 'Africa/Johannesburg'], ['+64', 'New Zealand', 'Pacific/Auckland'],
  ['+94', 'Sri Lanka', 'Asia/Colombo'], ['+977', 'Nepal', 'Asia/Kathmandu'], ['+880', 'Bangladesh', 'Asia/Dhaka'],
  ['+1', 'North America', null], ['+61', 'Australia', null],
] as [string, string, string | null][]).sort((a, b) => b[0].length - a[0].length);

/** "UAE · 6:42 pm" for a foreign number, the country alone when it spans zones,
 *  null for Indian or unrecognised numbers. */
export const nriLine = (mobile: string | null): string | null => {
  const m = (mobile || '').trim();
  if (!m.startsWith('+') || m.startsWith('+91')) return null;
  const hit = NRI_PREFIXES.find(([p]) => m.startsWith(p));
  if (!hit) return null;
  const [, country, zone] = hit;
  if (!zone) return country;
  const t = new Intl.DateTimeFormat('en-IN', { timeZone: zone, hour: 'numeric', minute: '2-digit', hour12: true }).format(new Date());
  return `${country} · ${t}`;
};

/** Absolute IST time for any core timestamp, zoned or naive-UTC. */
export const at = (iso: string) => fmtDayTime(parseCoreTime(iso).toISOString());

export const hasEnrichment = (lead: Lead): boolean => {
  const p = lead.enriched_profile;
  if (!p) return false;
  return Boolean(p.address || p.city || p.make || p.vehicles?.length || p.enrichment);
};

export const interestOf = (lead: Lead): string | null =>
  lead.tags?.find((t) => t.startsWith('interest:'))?.slice('interest:'.length) ?? null;

// ── Discovery ───────────────────────────────────────────────────────────────

const known = (v: unknown): v is string | number =>
  v != null && `${v}`.trim() !== '' && `${v}`.trim().toLowerCase() !== 'unknown';

/** The eight discovery fields, in the app's order; value null = not yet known. */
export const discoveryFields = (lead: Lead): { label: string; value: string | null }[] => {
  const cr = (n: number | null) => (n ? `${(n / 1e7).toFixed(1)}` : '?');
  const budget = lead.budget_range_min || lead.budget_range_max
    ? `₹${cr(lead.budget_range_min)} — ${cr(lead.budget_range_max)} Cr` : null;
  const pick = (v: unknown) => (known(v) ? `${v}`.replace(/_/g, ' ') : null);
  return [
    { label: 'Budget', value: budget },
    { label: 'Timeline', value: pick(lead.purchase_timeline) },
    { label: 'Motivation', value: pick(lead.primary_motivation) },
    { label: 'Family', value: known(lead.family_size) ? `${lead.family_size} members` : null },
    { label: 'Villa type', value: pick(lead.preferred_villa_type) },
    { label: 'Bank loan', value: pick(lead.needs_bank_loan) },
    { label: 'Investor type', value: pick(lead.investor_type) },
    { label: 'Buyer location', value: pick(lead.buyer_location) },
  ];
};

// ── What we know ────────────────────────────────────────────────────────────

export interface TopicRow { key: string; title: string; detail: string; stance: string | null; channel: string | null; at: string }

/** Blank topics and legacy-migration noise dropped; newest evidence first. */
export const understandingRows = (u: Understanding | null): TopicRow[] =>
  Object.entries(u?.understanding || {})
    .map(([key, topic]) => {
      const detail = (topic.detail || topic.value || '').trim();
      const latest = (topic.events || []).reduce<{ at?: string; channel?: string } | null>(
        (best, e) => (!best || (e.at || '') > (best.at || '') ? e : best), null);
      const channel = latest?.channel?.trim() || null;
      return { key, detail, channel, at: latest?.at || '', stance: topic.stance?.trim() || null,
        title: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) };
    })
    .filter((r) => r.detail && !(r.channel || '').toLowerCase().startsWith('legacy')
      && r.detail.toLowerCase() !== 'legacy:migration')
    .sort((a, b) => b.at.localeCompare(a.at));

/** Free-ish vocabulary, so match on intent keywords. */
export const stanceChip = (stance: string): string => {
  const s = stance.toLowerCase();
  if (['engage', 'positive', 'interest', 'keen', 'warm'].some((k) => s.includes(k))) return 'chip-forest';
  if (['resist', 'negative', 'object', 'concern', 'cold'].some((k) => s.includes(k))) return 'chip-brick';
  return 'chip-mute';
};

// ── Tags ────────────────────────────────────────────────────────────────────

const TAG_ORDER = ['manual', 'lead_type', 'visit_status', 'visit_logistics', 'contact_protocol', 'language', 'origin', 'disposition'];
const tagRank = (dim: string) => { const i = TAG_ORDER.indexOf(dim); return i < 0 ? TAG_ORDER.length : i; };

/** dimension:value tags only, in the app's dimension order; `interest:` lives
 *  on the stage picker, not here. */
export const orderedTags = (tags: string[] | null): { tag: string; dim: string; value: string }[] =>
  (tags || [])
    .filter((t) => t.includes(':') && !t.startsWith('interest:'))
    .map((tag) => ({ tag, dim: tag.slice(0, tag.indexOf(':')), value: tag.slice(tag.indexOf(':') + 1).replace(/_/g, ' ') }))
    .sort((a, b) => tagRank(a.dim) - tagRank(b.dim) || a.dim.localeCompare(b.dim) || a.value.localeCompare(b.value));

// ── Tasks ───────────────────────────────────────────────────────────────────

export const taskTitle = (t: Task): string =>
  (t.payload?.title || t.payload?.subject || '').toString().trim()
  || ({ call: 'Call', whatsapp: 'WhatsApp', site_visit: 'Site visit', other: 'To-do' } as Record<string, string>)[t.type]
  || t.type;

export const taskBriefing = (t: Task): string | null =>
  t.payload?.suggestion?.reason || t.payload?.agenda || t.payload?.summary || null;

export const isCheckingCall = (t: Task) => t.type === 'call' && t.status === 'open' && !!t.pending_result_activity_id;

export type DueState = 'checking' | 'late' | 'grace' | 'future';

/** checking → forest; late → brick; due but still inside its calling window →
 *  gold; not yet due → neutral. The window rule is the portal's isLate. */
export const dueState = (t: Task, windows: CallWindow[], now: Date = new Date()): DueState => {
  if (isCheckingCall(t)) return 'checking';
  if (isLate(t.due_at, windows, now)) return 'late';
  return new Date(t.due_at).getTime() <= now.getTime() ? 'grace' : 'future';
};

// ── Timeline ────────────────────────────────────────────────────────────────

export interface CallFacts {
  connectionResult: string | null;
  durationSeconds: number | null;
  recordings: Record<string, any>[];
  legacyRecordingUrl: string | null;
  notes: string | null;
}

/** The one decoder of a call payload — current top-level fields, or the older
 *  nested `outcome` shape. */
export const callFacts = (a: Activity): CallFacts => {
  const p = a.payload || {};
  const o = (p.outcome && typeof p.outcome === 'object') ? p.outcome : {};
  const recs = Array.isArray(p.recordings) && p.recordings.length ? p.recordings
    : Array.isArray(o.recordings) ? o.recordings : [];
  return {
    connectionResult: p.connection_result || null,
    durationSeconds: p.duration_seconds ?? o.duration_seconds ?? null,
    recordings: recs,
    legacyRecordingUrl: o.recording_url || p.recording_url || null,
    notes: p.notes || o.notes || null,
  };
};

/** Outbound, no talk time, nothing recorded. Inbound missed calls are the lead
 *  ringing us — a signal — and WhatsApp calls are operator-attested; neither folds. */
const isUnansweredDial = (a: Activity): boolean => {
  if (a.kind !== 'call' || a.direction !== 'outbound' || a.channel === 'whatsapp') return false;
  const f = callFacts(a);
  return !(f.durationSeconds || 0) && !f.recordings.length && !f.legacyRecordingUrl;
};

/** A closed task with no activity of its own shows up as a synthetic row. */
export interface ClosureEntry { kind: 'task_closed'; id: string; occurred_at: string; task: Task }

export type TimelineEntry =
  | { type: 'single'; key: string; at: string; activity: Activity }
  | { type: 'closure'; key: string; at: string; closure: ClosureEntry }
  | { type: 'dials'; key: string; at: string; dials: Activity[] };

export const closureEntries = (tasks: Task[]): ClosureEntry[] =>
  tasks
    .filter((t) => (t.status === 'done' || t.status === 'cancelled') && !t.result_activity_id && t.closed_reason !== 'rejected')
    .map((t) => ({ kind: 'task_closed' as const, id: t.id, task: t, occurred_at: t.payload?.selldo_acted_on || t.updated_at }));

/** Merge activities and closures newest-first, folding each run of adjacent
 *  unanswered dials into one entry. */
export const buildTimeline = (activity: Activity[], closures: ClosureEntry[]): TimelineEntry[] => {
  const ts = (iso: string) => parseCoreTime(iso).getTime();
  const merged: ({ a: Activity } | { c: ClosureEntry })[] = [
    ...activity.map((a) => ({ a })), ...closures.map((c) => ({ c })),
  ].sort((x, y) => ts('a' in y ? y.a.occurred_at : y.c.occurred_at) - ts('a' in x ? x.a.occurred_at : x.c.occurred_at));

  const out: TimelineEntry[] = [];
  let run: Activity[] = [];
  const flush = () => {
    if (run.length) out.push({ type: 'dials', key: `dials-${run[0].id}`, at: run[0].occurred_at, dials: run });
    run = [];
  };
  for (const m of merged) {
    if ('a' in m && isUnansweredDial(m.a)) { run.push(m.a); continue; }
    flush();
    if ('a' in m) out.push({ type: 'single', key: m.a.id, at: m.a.occurred_at, activity: m.a });
    else out.push({ type: 'closure', key: `closed-${m.c.id}`, at: m.c.occurred_at, closure: m.c });
  }
  flush();
  return out;
};

// ── Engagement ──────────────────────────────────────────────────────────────

export const fmtDwell = (ms: number): string | null => {
  if (!ms) return null;
  const s = Math.round(ms / 1000);
  return s >= 60 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${s}s`;
};

export const isEngaged = (l: LeadEngagementLink) =>
  !!l.open_count || l.frame_views > 0 || l.dwell_ms_total > 0 || !!l.button_clicked_at || !!l.submitted_at;
